// IC column header for the New Team Change view. Renders the slanted
// engineer name like `ResourceColHeader`, but with a hover-revealed
// action cluster (pencil + trash) that lets the user rename the IC
// or remove/mark-departed depending on the IC's origin (added in this
// session vs existing in the snapshot).
//
// Click pencil → the rotated <strong> is replaced by a horizontal
// <input> for the duration of the edit. Enter / blur commits; Escape
// cancels. The input sits unrotated so it's actually usable.

import { useEffect, useRef, useState } from 'react';

import { HoverAction } from './HoverAction.tsx';

const ROTATED_DISPLAY_MAX = 12;

function formatShortName(display: string): string {
  const parts = display.trim().split(/\s+/);
  if (parts.length < 2) return display;
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  const lastInitial = last[0]?.toUpperCase() ?? '';
  return lastInitial ? `${first} ${lastInitial}.` : first;
}

export function TeamChangeColumnHeader({
  display,
  col,
  row,
  zIndex,
  isAgent,
  isHire,
  isAdded,
  isDeparted,
  onRename,
  onDelete,
}: {
  display: string;
  col: number;
  row: number;
  /** Descending left→right so the rotated name overlaps the next column. */
  zIndex?: number;
  isAgent: boolean;
  isHire: boolean;
  isAdded: boolean;
  isDeparted: boolean;
  onRename: (next: string) => void;
  /** For added ICs: remove. For existing ICs: toggle departed flag. */
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(display);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(display);
  }, [display]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = () => {
    const trimmed = draft.trim();
    setEditing(false);
    if (trimmed && trimmed !== display) {
      onRename(trimmed);
    } else {
      setDraft(display);
    }
  };

  const classes = ['snap-grid__col-header', 'snap-grid__col-header--editable'];
  if (isAgent) classes.push('snap-grid__col-header--agent');
  if (isHire) classes.push('snap-grid__col-header--hire');
  if (isDeparted) classes.push('snap-grid__col-header--departed');

  const shown = isAgent
    ? display
    : display.length > ROTATED_DISPLAY_MAX
      ? formatShortName(display)
      : display;

  return (
    <div className={classes.join(' ')} style={{ gridRow: row, gridColumn: col, zIndex }}>
      {editing ? (
        <>
          <input
            ref={inputRef}
            className='snap-grid__col-header-input'
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setEditing(false);
                setDraft(display);
              }
            }}
            autoFocus
          />
          <span className='snap-edit-actions snap-grid__col-header-actions'>
            <HoverAction
              kind='delete'
              label={isAdded ? 'Remove IC' : 'Mark departed'}
              onClick={() => {
                setEditing(false);
                onDelete();
              }}
            />
          </span>
        </>
      ) : (
        <strong
          role='button'
          tabIndex={0}
          onClick={() => setEditing(true)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setEditing(true);
            }
          }}
        >
          {shown}
        </strong>
      )}
    </div>
  );
}
